import React, { useRef, useMemo, useEffect, useCallback } from 'react';

import { useReactTable, getCoreRowModel } from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';

import { Flex } from '@adsk/alloy-react-box';
import { keyBy } from '@adsk/alloy-react-helpers';
import {
  useTable,
  TableHeaderGroup,
  TableHeader,
  TableRow,
  TableCell,
  TABLE_SIZES,
  DEFAULT_TABLE_SIZE,
} from '@adsk/alloy-react-table';

import TableFooter from './TableFooter';
import {
  getSelectionColumn,
  getTableCellStyle,
  getTableHeaderStyle,
  selectTableRows,
} from '../helpers/utils';

const Table = ({
  multiple = true,
  enableColumnResizing = false,
  entities: data,
  columns: tableCols,
  selectedIds = [],
  disabledIds = [],
  tableSize = DEFAULT_TABLE_SIZE,
  reachEndThreshold = 200,
  loading,
  loadingMoreText,
  error,
  errorText,
  hideHeader = false,
  setSelection,
  onLoadMore,
  onTryAgain,
  ...rest
}) => {
  const tableRef = useRef(null);
  // Need a ref to save the value instead of a local var inside getSelectionColumn
  // as the function will be executed multiple times under some cases.
  const lastSelectRowIdxRef = useRef(null);
  const scrollProps = useRef({
    lastScrollTop: 0,
    lastScrollHeight: 0,
    dataChanged: true,
  });

  // Prefix selection column as shared config
  const columns = useMemo(
    () => (multiple ? [getSelectionColumn(), ...tableCols] : tableCols),
    [multiple, tableCols],
  );
  const rowSelection = useMemo(
    () =>
      selectedIds.reduce((pre, id) => {
        pre[id] = true;
        return pre;
      }, {}),
    [selectedIds],
  );

  const dataByKey = useMemo(() => keyBy(data, 'id'), [data]);
  const onRowSelectionChange = useCallback(
    (updaterOrValue) => {
      const curSelection =
        typeof updaterOrValue === 'function'
          ? updaterOrValue(rowSelection)
          : updaterOrValue;
      /**
       * Leave an entity with only id if not found in current data to avoid losing cross-view selection.
       * Also filter out disabled ids to avoid showing invalid selected count at modal footer.
       */
      setSelection(
        Object.keys(curSelection)
          .filter((id) => !disabledIds.includes(id))
          .map((id) => dataByKey[id] || { id }),
        false,
      );
    },
    [rowSelection, dataByKey, disabledIds, setSelection],
  );

  const table = useReactTable({
    columns,
    data,
    state: {
      rowSelection,
    },
    getRowId: (row) => row.id,
    getCoreRowModel: getCoreRowModel(),
    enableColumnResizing,
    enableSorting: false,
    enableRowSelection: (row) => !disabledIds.includes(row.id),
    enableMultiRowSelection: multiple,
    columnResizeMode: 'onChange',
    onRowSelectionChange,
  });

  const {
    getTableHeaderProps,
    getTableHeaderGroupProps,
    getTableRowProps,
    getTableCellProps,
  } = useTable({
    table,
    tableSize,
    enableFullRowClick: true,
  });

  // Virtualizer
  const { rows } = table.getRowModel();
  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => tableRef.current,
    estimateSize: () => tableSize.rowHeight || 48,
    overscan: 3,
  });
  const virtualRows = rowVirtualizer.getVirtualItems();
  const virtualHeight = rowVirtualizer.getTotalSize();
  const layouts = useMemo(
    () => ({
      paddingTop: virtualRows.length > 0 ? virtualRows?.[0]?.start || 0 : 0,
      paddingBottom:
        virtualRows.length > 0
          ? virtualHeight - (virtualRows?.[virtualRows.length - 1]?.end || 0)
          : 0,
    }),
    [virtualRows, virtualHeight],
  );

  const checkLoadMore = () => {
    // No need to check if no onLoadMore, no rows or no tableRef
    if (!onLoadMore || !rows || !tableRef.current) return;

    // No need to check if under loading or error state
    if ((loading || error) && rows.length > 0) return;

    const scrollHeight = tableRef.current.scrollHeight;
    const clientHeight = tableRef.current.clientHeight;
    const scrollTop = tableRef.current.scrollTop;
    const distanceToEnd =
      scrollHeight - scrollTop - clientHeight - reachEndThreshold;
    const { lastScrollHeight, dataChanged } = scrollProps.current;

    if (
      distanceToEnd <= 0 &&
      (dataChanged || scrollHeight !== lastScrollHeight)
    ) {
      scrollProps.current.dataChanged = false;
      scrollProps.current.lastScrollHeight = scrollHeight;
      onLoadMore();
    }
  };

  const handleScroll = () => {
    if (!tableRef.current) return;
    const scrollTop = tableRef.current.scrollTop;
    if (scrollTop > scrollProps.current.lastScrollTop) checkLoadMore();
    scrollProps.current.lastScrollTop = scrollTop;
  };

  const handleRowSelected = useCallback(
    (row, val, e) => {
      const isShiftSelect = e.shiftKey || e.nativeEvent?.shiftKey;
      selectTableRows({
        val,
        multiple,
        isShiftSelect,
        table,
        row,
        lastSelectRowIdxRef,
      });
    },
    [multiple, table],
  );

  useEffect(() => {
    scrollProps.current.dataChanged = true;
  }, [data]);

  delete rest.onUpdate;
  delete rest.onChange;

  return (
    <Flex
      position="relative"
      width="100%"
      height="100%"
      overflow="auto"
      flexDirection="column"
      {...rest}
      onScroll={handleScroll}
      ref={tableRef}
    >
      <table
        style={{
          // fixes sticky header jumping: https://stackoverflow.com/questions/70772981/table-sticky-header-jumps
          borderSpacing: 0,
        }}
      >
        {!hideHeader && (
          <thead style={{ position: 'sticky', margin: 0, top: 0, zIndex: 1 }}>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableHeaderGroup
                key={headerGroup.id}
                {...getTableHeaderGroupProps(headerGroup)}
              >
                {headerGroup.headers.map((header) => (
                  <TableHeader
                    key={header.id}
                    {...getTableHeaderProps(header)}
                    {...getTableHeaderStyle(header)}
                  />
                ))}
              </TableHeaderGroup>
            ))}
          </thead>
        )}
        <tbody>
          {layouts.paddingTop > 0 && (
            <tr>
              <td style={{ height: `${layouts.paddingTop}px` }} />
            </tr>
          )}
          {virtualRows.map((virtualRow) => {
            const row = rows[virtualRow.index];
            const cells = row.getVisibleCells();
            const rowProps = getTableRowProps(row);
            return (
              <TableRow
                key={row.id}
                {...rowProps}
                disabled={!rowProps.canSelect}
                toggleSelected={(...args) => handleRowSelected(row, ...args)}
              >
                {cells.map((cell) => (
                  <TableCell
                    key={cell.id}
                    {...getTableCellProps(cell)}
                    {...getTableCellStyle(cell)}
                  />
                ))}
              </TableRow>
            );
          })}
          {layouts.paddingBottom > 0 && (
            <tr>
              <td style={{ height: `${layouts.paddingBottom}px` }} />
            </tr>
          )}
        </tbody>
      </table>
      <TableFooter
        loading={loading}
        error={error}
        loadingMoreText={loadingMoreText}
        errorText={errorText}
        onRetry={onTryAgain}
      />
    </Flex>
  );
};

export { Table as default, TABLE_SIZES };
